(() => {
  const selectRandom = (options) => {
    if (!Array.isArray(options) || options.length === 0) return undefined;
    const pool = options.filter((option) => typeof option === 'string' && option.trim() !== '');
    if (pool.length === 0) return undefined;
    const index = Math.floor(Math.random() * pool.length);
    return pool[index];
  };

  const parseOptions = (raw) => {
    if (!raw) return [];
    try {
      const parsed = JSON.parse(raw);
      if (Array.isArray(parsed)) {
        const sanitized = parsed
          .filter((value) => typeof value === 'string' && value.trim() !== '')
          .map((value) => value.trim());
        return Array.from(new Set(sanitized));
      }
    } catch (_error) {
      /* ignore parse errors */
    }
    return [];
  };

  const removePendingState = (video) => {
    video.classList.remove('banner__video--pending');
  };

  const parseManifest = (raw) => {
    if (!raw) return [];
    try {
      const parsed = JSON.parse(raw);
      if (Array.isArray(parsed)) {
        return parsed.filter((entry) => entry && typeof entry.src === 'string');
      }
    } catch (_error) {
      /* ignore parse errors */
    }
    return [];
  };

  const getPosterForSrc = (manifest, src) => {
    if (!Array.isArray(manifest) || manifest.length === 0) return '';
    if (typeof src !== 'string' || src.trim() === '') return '';
    const match = manifest.find(
      (entry) =>
        entry &&
        typeof entry.src === 'string' &&
        entry.src.trim() === src.trim() &&
        typeof entry.poster === 'string' &&
        entry.poster.trim() !== ''
    );
    return match ? match.poster.trim() : '';
  };

  const applyPosterForSrc = (video, manifest, src) => {
    const poster = getPosterForSrc(manifest, src);
    if (poster) {
      video.setAttribute('poster', poster);
      video.dataset.posterReady = 'true';
    } else if (!video.getAttribute('poster')) {
      video.dataset.posterReady = 'false';
    }
  };

  const getCrossfadeMsForSrc = (manifest, src) => {
    if (!Array.isArray(manifest) || manifest.length === 0) return 0;
    if (typeof src !== 'string' || src.trim() === '') return 0;
    const match = manifest.find(
      (entry) => entry && typeof entry.src === 'string' && entry.src.trim() === src.trim()
    );
    const ms = match && typeof match.crossfadeMs === 'number' ? match.crossfadeMs : 0;
    return ms > 0 ? ms : 0;
  };

  const isLowPowerDevice = () => {
    try {
      if (window.matchMedia('(max-width: 749px)').matches) return true;
      const conn = navigator.connection || navigator.mozConnection || navigator.webkitConnection;
      if (conn && (conn.saveData || /2g/.test(conn.effectiveType || ''))) return true;
      if (
        typeof navigator.hardwareConcurrency === 'number' &&
        navigator.hardwareConcurrency > 0 &&
        navigator.hardwareConcurrency <= 2
      ) {
        return true;
      }
    } catch (_error) {
      /* unknown - default to allowing the crossfade */
    }
    return false;
  };

  const attemptPlayback = (video) => {
    const playPromise = video.play();
    if (playPromise && typeof playPromise.catch === 'function') {
      playPromise.catch(() => {});
    }
  };

  const waitForReadyState = (video, callback) => {
    if (typeof callback !== 'function') return;
    if (video.readyState >= 2) {
      callback();
      return;
    }

    const handleLoaded = () => {
      video.removeEventListener('loadeddata', handleLoaded);
      callback();
    };

    video.addEventListener('loadeddata', handleLoaded);
  };

  const crossfadeCleanup = new WeakMap();

  const teardownCrossfade = (video) => {
    const cleanup = crossfadeCleanup.get(video);
    if (typeof cleanup === 'function') cleanup();
    crossfadeCleanup.delete(video);
  };

  // Reveals the true start of the clip on *every* loop, not just the first
  // one: rather than starting a shadow clone once and letting both elements
  // free-run forever on native `loop` with a fixed phase offset (which only
  // stays correct for a single cycle - the offset drifts relative to "which
  // element is visible" after the first swap), each cycle explicitly resets
  // whichever element is about to be revealed back to time 0 right before
  // showing it. The two <video> elements swap the "front / back" CSS roles
  // every cycle instead of keeping a fixed identity, so only one element is
  // ever actively decoding/playing at a time (plus a brief ~crossfadeMs
  // window where both are), keeping decode overhead low.
  //
  // Only the FRONT layer's opacity ever animates (1 -> 0, via
  // .is-crossfade-hidden). The back layer always sits at full opacity,
  // stacked underneath (z-index) - it's revealed simply by the front fading
  // away on top of it. Animating both layers' opacity in opposite
  // directions at once (as an earlier version did) causes a dip-to-black
  // artifact: with nothing opaque behind either layer, the combined alpha
  // at the transition's midpoint is far below 100%.
  const setupCrossfadeLoop = (video, crossfadeMs) => {
    if (!video || !(crossfadeMs > 0)) return undefined;
    const parent = video.parentElement;
    if (!parent) return undefined;

    const sourceEl = video.querySelector('source');
    const src = sourceEl ? sourceEl.getAttribute('src') || '' : video.currentSrc || '';
    if (!src) return undefined;

    const shadow = document.createElement('video');
    shadow.className = 'banner__video--crossfade-shadow';
    shadow.muted = true;
    shadow.setAttribute('muted', '');
    shadow.setAttribute('playsinline', '');
    shadow.setAttribute('preload', 'auto');
    shadow.setAttribute('tabindex', '-1');
    shadow.setAttribute('aria-hidden', 'true');
    shadow.setAttribute('disablepictureinpicture', '');
    shadow.setAttribute('controlslist', 'nodownload nofullscreen noremoteplayback');
    const shadowSource = document.createElement('source');
    shadowSource.setAttribute('type', 'video/mp4');
    shadowSource.setAttribute('src', src);
    shadow.appendChild(shadowSource);
    shadow.load();
    parent.insertBefore(shadow, video.nextSibling);

    const durationSeconds = crossfadeMs / 1000;
    video.style.setProperty('--wrpg-crossfade-duration', `${durationSeconds}s`);
    shadow.style.setProperty('--wrpg-crossfade-duration', `${durationSeconds}s`);

    let frontEl = video;
    let backEl = shadow;
    let armed = false;
    let stopped = false;
    let rafId;
    let swapTimer;

    const startCrossfade = (remainingMs) => {
      armed = true;
      // backEl is already at full opacity (its default, unanimated) and
      // sits at a lower z-index than frontEl - starting it playing from 0
      // has no visible effect yet, since frontEl (opaque, on top) still
      // fully covers it.
      backEl.currentTime = 0;
      const playPromise = backEl.play();
      if (playPromise && typeof playPromise.catch === 'function') playPromise.catch(() => {});

      // The only opacity animation in this whole system: frontEl fades from
      // 1 to 0, gradually revealing backEl (already fully opaque) beneath.
      frontEl.classList.add('is-crossfade-hidden');

      swapTimer = window.setTimeout(() => {
        const oldFront = frontEl;
        const oldBack = backEl;

        // oldFront is now fully transparent (its fade finished) - relabel
        // it to the "back" role (lower z-index, no opacity override) so
        // it's ready to be armed again next cycle. Since it's invisible
        // either way at this instant, the relabeling itself is unnoticeable.
        oldFront.classList.remove('is-crossfade-hidden', 'banner__video');
        oldFront.classList.add('banner__video--crossfade-shadow');
        oldFront.pause();

        // oldBack has been fully opaque and visible (revealed by oldFront's
        // fade) this whole time - relabel it to the "front" role (higher
        // z-index) with no visual change, since it was already opaque.
        oldBack.classList.remove('banner__video--crossfade-shadow');
        oldBack.classList.add('banner__video');

        frontEl = oldBack;
        backEl = oldFront;
        armed = false;
      }, Math.max(0, remainingMs));
    };

    const tick = () => {
      if (stopped) return;
      const duration = frontEl.duration;
      if (!armed && Number.isFinite(duration) && duration > 0) {
        const remainingMs = (duration - frontEl.currentTime) * 1000;
        if (remainingMs <= crossfadeMs) {
          startCrossfade(remainingMs);
        }
      }
      rafId = window.requestAnimationFrame(tick);
    };

    rafId = window.requestAnimationFrame(tick);

    return () => {
      stopped = true;
      if (rafId) window.cancelAnimationFrame(rafId);
      if (swapTimer) window.clearTimeout(swapTimer);
      shadow.pause();
      shadow.remove();
      video.classList.remove('is-crossfade-hidden');
      video.classList.add('banner__video');
      video.style.removeProperty('--wrpg-crossfade-duration');
    };
  };

  const applyCrossfadeIfNeeded = (video, manifest, src) => {
    teardownCrossfade(video);
    const crossfadeMs = getCrossfadeMsForSrc(manifest, src);
    if (crossfadeMs <= 0 || isLowPowerDevice()) return;
    const cleanup = setupCrossfadeLoop(video, crossfadeMs);
    if (typeof cleanup === 'function') crossfadeCleanup.set(video, cleanup);
  };

  const playbackVisibilityCleanup = new WeakMap();

  const bindPlaybackToVisibility = (video) => {
    if (!video || playbackVisibilityCleanup.has(video)) return;
    if (!('IntersectionObserver' in window)) return;

    const host = video.closest('.banner, .shopify-section') || video;
    const pauseShadow = () => {
      const parent = video.parentElement;
      if (!parent) return;
      parent.querySelectorAll('.banner__video--crossfade-shadow').forEach((shadow) => shadow.pause());
    };
    const playShadow = () => {
      const parent = video.parentElement;
      if (!parent) return;
      parent.querySelectorAll('.banner__video--crossfade-shadow').forEach((shadow) => attemptPlayback(shadow));
    };

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            attemptPlayback(video);
            playShadow();
          } else {
            video.pause();
            pauseShadow();
          }
        });
      },
      { threshold: 0, rootMargin: '0px' }
    );
    observer.observe(host);
    playbackVisibilityCleanup.set(video, () => observer.disconnect());
  };

  const finalizeVideo = (video, manifest, src) => {
    removePendingState(video);
    attemptPlayback(video);
    applyCrossfadeIfNeeded(video, manifest, src);
    bindPlaybackToVisibility(video);
  };

  const ensureSourceElement = (video) => {
    let source = video.querySelector('source');
    if (source) return source;
    source = document.createElement('source');
    source.setAttribute('type', 'video/mp4');
    video.appendChild(source);
    return source;
  };

  const setVideoSource = (video, sourceElement, src, fallback, manifest) => {
    if (!src) {
      removePendingState(video);
      return;
    }

    const handleError = () => {
      video.removeEventListener('error', handleError);
      if (fallback && fallback !== sourceElement.getAttribute('src')) {
        sourceElement.setAttribute('src', fallback);
        applyPosterForSrc(video, manifest, fallback);
        video.load();
        waitForReadyState(video, () => finalizeVideo(video, manifest, fallback));
      } else {
        removePendingState(video);
      }
    };

    if (fallback) {
      video.addEventListener('error', handleError, { once: true });
    }

    if (sourceElement.getAttribute('src') !== src) {
      sourceElement.setAttribute('src', src);
      applyPosterForSrc(video, manifest, src);
      video.load();
    }

    waitForReadyState(video, () => finalizeVideo(video, manifest, src));
  };

  const applyRandomVideo = (video) => {
    const options = parseOptions(video.dataset.randomVideoOptions);
    const fallback = (video.dataset.randomVideoFallback || '').trim();
    const manifest = parseManifest(video.dataset.randomVideoManifest);
    const source = ensureSourceElement(video);
    if (!source) {
      removePendingState(video);
      return;
    }

    const currentSrc = source.getAttribute('src') ? source.getAttribute('src').trim() : '';
    const pool = Array.from(
      new Set(
        [...options, fallback, currentSrc]
          .filter((value) => typeof value === 'string' && value.trim() !== '')
          .map((value) => value.trim())
      )
    );
    const initialSrc = (video.dataset.randomVideoInitial || '').trim();
    let targetSrc = '';
    if (initialSrc && pool.includes(initialSrc)) {
      targetSrc = initialSrc;
      video.dataset.randomVideoInitial = '';
    } else {
      targetSrc = selectRandom(pool) || initialSrc || fallback || currentSrc;
    }

    if (!targetSrc) {
      removePendingState(video);
      return;
    }

    applyPosterForSrc(video, manifest, targetSrc);

    if (targetSrc === currentSrc) {
      waitForReadyState(video, () => finalizeVideo(video, manifest, targetSrc));
      return;
    }

    setVideoSource(video, source, targetSrc, fallback, manifest);
  };

  const lazyVideoCleanup = new WeakMap();

  const loadLazyVideoImmediately = (video) => {
    if (!video || video.dataset.randomVideoLoadState === 'loaded') return;
    const cleanup = lazyVideoCleanup.get(video);
    if (typeof cleanup === 'function') {
      cleanup();
      lazyVideoCleanup.delete(video);
    }
    video.dataset.randomVideoLoadState = 'loaded';
    applyRandomVideo(video);
  };

  const getLazyIntersectionObserver = (() => {
    let observer;
    return () => {
      if (observer) return observer;
      if (!('IntersectionObserver' in window)) return null;
      observer = new IntersectionObserver(
        (entries, obs) => {
          entries.forEach((entry) => {
            if (!entry.isIntersecting) return;
            obs.unobserve(entry.target);
            loadLazyVideoImmediately(entry.target);
          });
        },
        { rootMargin: '200px', threshold: 0.1 }
      );
      return observer;
    };
  })();

  const queueLazyVideoLoad = (video) => {
    if (!video) return;
    const loadState = video.dataset.randomVideoLoadState;
    if (loadState === 'queued' || loadState === 'loaded') return;
    video.dataset.randomVideoLoadState = 'queued';

    const observer = getLazyIntersectionObserver();

    let fallbackTimer;
    let fallback;
    const cleanup = () => {
      window.removeEventListener('load', fallback);
      window.removeEventListener('scroll', fallback);
      if (fallbackTimer) {
        clearTimeout(fallbackTimer);
        fallbackTimer = undefined;
      }
    };

    fallback = () => {
      if (video.dataset.randomVideoLoadState === 'loaded') {
        cleanup();
        return;
      }
      cleanup();
      if (observer) {
        observer.unobserve(video);
      }
      loadLazyVideoImmediately(video);
    };

    if (observer) {
      observer.observe(video);
    }

    window.addEventListener('load', fallback, { once: true });
    window.addEventListener('scroll', fallback, { once: true });
    fallbackTimer = window.setTimeout(() => fallback(), 20000);
    lazyVideoCleanup.set(video, cleanup);
  };

  const initRandomVideos = (scope = document) => {
    scope.querySelectorAll('[data-random-video-options]').forEach((video) => {
      if (video.dataset.lazyLoad === 'true') {
        queueLazyVideoLoad(video);
      } else {
        applyRandomVideo(video);
      }
    });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initRandomVideos());
  } else {
    initRandomVideos();
  }

  document.addEventListener('shopify:section:load', (event) => {
    initRandomVideos(event.target);
  });
})();
